// Lib Imports
import React, { useEffect, useMemo, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Heading, ThemeContext } from 'grommet';
import { withRouter } from 'react-router-dom';

// CORE Imports
import EventEmitter from 'granite-admin/utils/event-emitter';
import REGISTER_MESSAGES from 'granite-admin/messages/register';
import { ConfigContext } from 'granite-admin/core/components/ConfigProvider';
import RESET_MESSAGES from 'granite-admin/messages/forgotPassword';
import { useToast } from 'granite-admin/core/components/Toast';
import Steps from 'granite-admin/core/components/Steps';
import SplitLayout from 'granite-admin/core/components/SplitLayout';

// Application Imports
import { REGISTER_EVENTS, RESET_EVENTS } from 'granite-admin/accounts/controllers/constants';
import { registerClicked, submitTokenClicked, resendOtp } from 'granite-admin/accounts/controllers/user';
import RegisterForm from './components/RegisterForm';
import SubmitTokenForm from './components/SubmitTokenForm';
import ActivationSuccess from './components/ActivationSuccess';
const type = process.env.REACT_APP_REGISTER_TYPE || 'email';
const projectName = process.env.REACT_APP_PROJECT_NAME || 'Granite';
function listenEvents(eventEmitter, setActiveStep, setRetry, successToast, errorToast) {
  const observable = eventEmitter.getObservable();
  const subscription = observable.subscribe(event => {
    switch (event.type) {
      case REGISTER_EVENTS.REGISTER_SUCCESS:
        successToast(REGISTER_MESSAGES.REGISTER_SUCCESS);
        setActiveStep(1);
        break;
      case REGISTER_EVENTS.REGISTER_FAILURE:
        errorToast(REGISTER_MESSAGES.REGISTER_FAILURE);
        break;
      case RESET_EVENTS.RESET_TOKEN_SUCCESS:
        setTimeout(() => {}, 5000);
        setActiveStep(2);
        break;
      case RESET_EVENTS.RESET_TOKEN_FAILURE:
        setRetry(true);
        errorToast(RESET_MESSAGES.RESET_TOKEN_FAILURE);
        break;
      case 'OTP_SEND_SUCCESS':
        successToast('OTP sent successfully');
        break;
      case 'OTP_SEND_FAILURE':
        errorToast('Failed to send OTP');
        break;
      default:
        break;
    }
  });
  return subscription;
}
const Register = ({
  history
}) => {
  const [retry, setRetry] = useState(false);
  const {
    successToast,
    errorToast
  } = useToast();
  const eventEmitter = useMemo(() => new EventEmitter(), []);
  const defaultActiveStep = 0;
  const [activeStep, setActiveStep] = useState(defaultActiveStep);
  const [emailOrPhone, setEmailOrPhone] = useState({
    email: undefined
  });
  const formikRef = useRef();
  const config = useContext(ConfigContext);
  const theme = useContext(ThemeContext);
  const {
    registerHeading
  } = config?.registerDetails;
  const {
    SideContent
  } = config?.sideContentSettings || '';
  const {
    sideProps,
    logoWidth,
    sideWidth,
    configMainPropsRegister
  } = theme?.sideContentSettings || '';
  const headings = useMemo(() => {
    return [registerHeading ? `${registerHeading}` : 'Sign Up', 'Verification'];
  }, [registerHeading]);
  const subHeadings = useMemo(() => {
    return [registerHeading ? null : `Sign in to your ${projectName}`, `Enter the One Time Password (OTP) sent on your ${type === 'email' ? 'email address' : 'phone number'}.`];
  }, [registerHeading]);
  const getSteps = () => {
    const registerOnSubmit = async (values, {
      setSubmitting,
      setErrors
    }) => {
      setErrors({});
      setSubmitting(true);
      try {
        await registerClicked(eventEmitter, values, type);
        // storing emailOrPhone of user to allow resend token
        setEmailOrPhone({
          [type]: values[type]
        });
      } catch (e) {
        if (e.errors) setErrors(e.errors);
      }
      setSubmitting(false);
    };
    const submitTokenFormOnSubmit = async (values, {
      setSubmitting,
      setErrors
    }) => {
      setErrors({});
      setSubmitting(true);
      try {
        let obj = {
          [type]: emailOrPhone[type],
          token: values.token
        };
        await submitTokenClicked(eventEmitter, obj);
      } catch (e) {
        if (e.errors) setErrors(e.errors);
      }
      setSubmitting(false);
    };
    const resendToken = async () => {
      try {
        let obj = {
          [type]: emailOrPhone[type]
        };
        await resendOtp(eventEmitter, obj);
        formikRef.current.setFieldValue('token', '');
      } catch (e) {
        console.log(e);
        // if (e.errors) setErrors(e.errors);
      }
      // setSubmitting(false);
    };

    return [{
      content: /*#__PURE__*/React.createElement(RegisterForm, {
        onSubmit: registerOnSubmit,
        type: type,
        config: config
      }),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, {
      content: /*#__PURE__*/React.createElement(SubmitTokenForm, {
        onSubmit: submitTokenFormOnSubmit,
        resendToken: resendToken,
        retry: retry,
        formikRef: formikRef
      }),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }, {
      content: /*#__PURE__*/React.createElement(ActivationSuccess, {
        history: history
      }),
      icon: /*#__PURE__*/React.createElement(React.Fragment, null)
    }];
  };
  useEffect(function init() {
    const subscription = listenEvents(eventEmitter, setActiveStep, setRetry, successToast, errorToast);
    return () => subscription.unsubscribe();
  }, [eventEmitter, history, successToast, errorToast]);
  return /*#__PURE__*/React.createElement(SplitLayout, {
    sideContent: SideContent ? /*#__PURE__*/React.createElement(SideContent, null) : null,
    sideProps: sideProps,
    logoWidth: logoWidth,
    sideWidth: sideWidth
    // formPad={formPad}
    ,
    mainProps: configMainPropsRegister ?? {
      style: {
        marginTop: '3%'
      }
    },
    mainContent: /*#__PURE__*/React.createElement(Box, {
      width: "large",
      flex: "grow"
    }, headings[activeStep] && /*#__PURE__*/React.createElement(Heading, {
      level: 3
    }, headings[activeStep]), subHeadings[activeStep] && /*#__PURE__*/React.createElement(Heading, {
      level: 5,
      size: "large",
      color: "dark-2"
    }, subHeadings[activeStep]), /*#__PURE__*/React.createElement(Steps, {
      margin: "xsmall",
      steps: getSteps(),
      activeStep: activeStep,
      onChange: () => false,
      navigationVisible: false
    }))
  });
};
Register.propTypes = {
  // dispatch: PropTypes.func,
  history: PropTypes.object
};
export default withRouter(Register);